import { Agreement } from '@economic/react-app';
import { t } from '@lingui/macro';
import { format, normalizeToDate } from '~/app/shared/util/utilities/date';
import qs from 'qs';
import internalApiClient from '~/app/shared/util/api/internalApiClient';
import {
    CustomerLedgerCardsFilterState,
    CustomerLedgerCardsTableData,
    CustomerLedgerCardsDataResponse,
    CustomerLedgerCardsFilterPayload,
    CustomerLedgerCardsColumn,
    AddPeriod,
} from '../types/types';

export function getInitialFilter() {
    const now = new Date();

    const initialFilter: CustomerLedgerCardsFilterState = {
        fromDate: new Date(now),
        toDate: new Date(now.setMonth(now.getMonth() + 1)),
        currency: 'Converted',
        showAccountsWithNoBalance: false,
        showOpenOnly: false,
        layoutGroup: 0,
        includeNonBookedEntries: false,
        addPeriodTo: 0,
    };

    return initialFilter;
}

export const fetch = async (filterState?: CustomerLedgerCardsFilterState): Promise<CustomerLedgerCardsTableData[]> => {
    const response = await internalApiClient.post<CustomerLedgerCardsDataResponse[]>(
        `/reports/customers/ledger-cards`,
        filterState
    );
    return mapResponseToTableData(response.data);
};

export const mapResponseToTableData = (response: CustomerLedgerCardsDataResponse[]): CustomerLedgerCardsTableData[] => {
    return response.map(
        data =>
            ({
                customerNumber: data.customerNumber,
                customerName: data.customerName,
                customerEmail: data.customerEmail,
                currency: data.currency,
                date: data.date,
                voucherNumber: data.voucherNumber,
                journalEntryType: data.journalEntryType,
                journalEntryNumber: data.journalEntryNumber,
                isArtificial: data.isArtificial,
                text: data.text,
                amount: data.amount,
                balance: data.balance,
                invoiceNumber: data.invoiceNumber,
                draftNumber: data.draftNumber,
                voucherDisplayNumber: data.voucherDisplayNumber,
                imageNumber: data.imageNumber,
                appendixNumber: data.appendixNumber,
                genNumber: data.genNumber,
            }) as CustomerLedgerCardsTableData
    );
};

export const mapFrontEndFiltersIntoBackEnd = (filterState: CustomerLedgerCardsFilterState) => {
    const backendFilters: CustomerLedgerCardsFilterPayload = {
        fromDate: normalizeToDate(filterState.fromDate),
        toDate: normalizeToDate(filterState.toDate),
        currency: filterState.currency,
        fromCustomer: filterState?.fromCustomer,
        toCustomer: filterState?.toCustomer,
        showAccountsWithNoBalance: filterState.showAccountsWithNoBalance,
        fromGroup: filterState?.fromCustomerGroup,
        toGroup: filterState?.toCustomerGroup,
        showOpenOnly: filterState.showOpenOnly,
        heading: filterState.heading,
        text: filterState.text,
        layoutGroup: filterState.layoutGroup,
        includeNonBookedEntries: filterState.includeNonBookedEntries,
        addPeriodTo: filterState.addPeriodTo,
    };

    return backendFilters;
};

export function getExportColumns(filter: CustomerLedgerCardsFilterState, baseCurrency: string) {
    const hasCurrencyFilter = filter.currency !== 'Converted';
    const columns = [
        {
            accessor: CustomerLedgerCardsColumn.Date,
            columnHeader: t`Date`,
        },
        {
            accessor: CustomerLedgerCardsColumn.VoucherNumber,
            columnHeader: t`Entry`,
        },
        {
            accessor: CustomerLedgerCardsColumn.InvoiceNumber,
            columnHeader: t`Invoice`,
        },
        {
            accessor: CustomerLedgerCardsColumn.JournalEntryType,
            columnHeader: t`Type`,
        },
        {
            accessor: CustomerLedgerCardsColumn.Text,
            columnHeader: t`Text`,
        },
        {
            accessor: CustomerLedgerCardsColumn.Currency,
            columnHeader: t`Currency`,
        },
        {
            accessor: CustomerLedgerCardsColumn.Amount,
            columnHeader: `${t`Amount`} (${hasCurrencyFilter ? filter.currency : baseCurrency})`,
        },
        {
            accessor: CustomerLedgerCardsColumn.Balance,
            columnHeader: `${t`Balance`} (${hasCurrencyFilter ? filter.currency : baseCurrency})`,
        },
    ];

    return columns;
}

export function getExportHeadings(agreement, reportHeading: string) {
    return ['', `${agreement.number} - ${agreement.name}`, reportHeading];
}

export const buildPeriodText = (filterState: CustomerLedgerCardsFilterState) => {
    const title = t`Ledger card`;
    const fromDate = format(filterState.fromDate, 'dd.mm.yy');
    const toDate = format(filterState.toDate, 'dd.mm.yy');

    const message = `${title} ${t`for the period`} ${fromDate} - ${toDate}`;
    return message;
};

export const showStatementsOfAccounts = (
    customerNumbers: string,
    customerEmails: string,
    agreement: Agreement,
    filterState?: CustomerLedgerCardsFilterState
): string => {
    if (customerNumbers.length === 0 || filterState === undefined) return '';

    const periodText = buildPeriodText(filterState);
    const queryParams = {
        action: 'show',
        ops: agreement.setupId,
        overskrift: filterState.addPeriodTo === AddPeriod.Heading ? `${periodText} ${filterState.heading}` : filterState.heading,
        tekst: filterState.addPeriodTo === AddPeriod.TextField ? `${periodText} ${filterState.text}` : filterState.text,
        debitornr: customerNumbers,
        debitoremail: customerEmails,
        valuta: filterState.currency !== 'Converted' ? filterState.currency : 'omregnet',
        type: 'kontoudtog',
        fradato: format(filterState.fromDate, 'dd-mm-yyyy'),
        tildato: format(filterState.toDate, 'dd-mm-yyyy'),
        kunaabne: filterState.showOpenOnly,
        layoutgruppe: filterState?.layoutGroup ?? 0,
    };

    const queryString = qs.stringify(queryParams);
    const uri = `/secure/include/visdebitorkontorykker.asp?${queryString}`;
    return uri;
};

export const openStatementsOfAccountsWindow = (
    rows: CustomerLedgerCardsTableData[],
    agreement: Agreement,
    filterState: CustomerLedgerCardsFilterState
) => {
    const customerNumbers = Array.from(new Set(rows.map(row => row.customerNumber))).join(',');
    const customerEmails = Array.from(new Set(rows.map(row => row.customerEmail))).join(',');

    const url = showStatementsOfAccounts(customerNumbers, customerEmails, agreement, filterState);
    window.open(url, '_blank', 'noreferrer');
};
